import { useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../ui/Modal';
import { ConnectGuide } from './ConnectGuide';
import { copyText } from './copyText';
import { detectPlatform } from './guideData';
import styles from './ConnectModal.module.css';

interface ConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscriptionUrl: string;
  title?: string;
  /** Ключ уже скопирован при нажатии «Подключить» (копируем в обработчике клика — Safari
   *  не даёт писать в буфер вне жеста пользователя) */
  copied?: boolean;
}

/** Окно «Подключиться»: ссылка подписки + инструкция под устройство, с которого зашли */
export function ConnectModal({
  isOpen,
  onClose,
  subscriptionUrl,
  title = 'Подключиться',
  copied = false,
}: ConnectModalProps) {
  // Повторное копирование из окна; при каждом открытии сбрасываем к тому, что пришло с карточки
  const [recopied, setRecopied] = useState<boolean | null>(null);
  const [prevOpen, setPrevOpen] = useState(isOpen);
  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    setRecopied(null);
  }
  const isCopied = recopied ?? copied;

  const handleCopy = async () => {
    const ok = await copyText(subscriptionUrl);
    setRecopied(ok);
    if (ok) toast.success(isCopied ? 'Скопировано ещё раз' : 'Ключ скопирован');
    else toast.error('Не удалось скопировать, выделите ссылку вручную');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <header className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.subtitle}>
          {isCopied
            ? 'Ключ уже скопирован. Осталось скачать приложение, вставить ключ и нажать «Подключить»'
            : 'Три шага: скачайте приложение, добавьте в него ключ, нажмите «Подключить»'}
        </p>
      </header>

      <div className={styles.linkRow}>
        <span className={styles.linkLabel}>Ссылка</span>
        <span className={styles.link}>{subscriptionUrl}</span>
        {isCopied ? (
          <button
            type="button"
            className={styles.copiedBadge}
            onClick={handleCopy}
            title="Скопировать ещё раз"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Ключ скопирован
          </button>
        ) : (
          <button type="button" className={styles.copyBtn} onClick={handleCopy}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            Скопировать
          </button>
        )}
      </div>

      {isOpen && (
        <ConnectGuide
          subscriptionUrl={subscriptionUrl}
          initialPlatform={detectPlatform(navigator.userAgent)}
          copied={isCopied}
        />
      )}
    </Modal>
  );
}
